// Ponesi first-party analytics client.
// Privacy-safe: a random anonymous_id (no fingerprinting), a session id that expires after
// 30 min of inactivity and is shared across tabs, first-touch UTM/referrer, and a queue that
// batches events. Every event carries a stable event_id so rerenders, double clicks, retries
// and refreshes can never double-count. Failures are swallowed — analytics never breaks the app.
import { API } from "./api";

const AID_KEY = "ponesi_aid";
const SESSION_KEY = "ponesi_an_session";
const SESSION_TTL_MS = 30 * 60 * 1000;
const FLUSH_MS = 1500;
const MAX_BATCH = 25;

const uuid = () =>
  (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const ls = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch {} },
};

function anonymousId() {
  let id = ls.get(AID_KEY);
  if (!id) { id = uuid(); ls.set(AID_KEY, id); }
  return id;
}

function utmFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    const out = {};
    ["source", "medium", "campaign", "content", "term"].forEach((k) => {
      const v = p.get(`utm_${k}`);
      if (v) out[k] = v.slice(0, 100);
    });
    return out;
  } catch { return {}; }
}

// Captured at module load — the router / city onboarding may strip the query string before
// the first React effect runs, so first-touch attribution must be read here, not lazily.
const INITIAL_UTM = utmFromUrl();
const INITIAL_PATH = (() => { try { return window.location.pathname.slice(0, 200); } catch { return "/"; } })();

function normLang(v) {
  const m = /^[a-z]{2}(-[A-Z]{2})?/i.exec(String(v || ""));
  return m ? m[0] : "sr";
}

function deviceType() {
  try {
    const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone;
    if (standalone) return "pwa";
    const ua = navigator.userAgent.toLowerCase();
    if (/ipad|tablet/.test(ua) && !/mobi/.test(ua)) return "tablet";
    if (/mobi|iphone|android/.test(ua)) return "mobile";
    return "desktop";
  } catch { return "unknown"; }
}

// Session is stored in localStorage so multiple tabs share ONE session; it rotates after
// 30 minutes of inactivity. First-touch attribution is captured only when the session is born.
function session() {
  let s = null;
  try { s = JSON.parse(ls.get(SESSION_KEY) || "null"); } catch {}
  const now = Date.now();
  const fresh = s && s.id && now - (s.last || 0) < SESSION_TTL_MS;
  if (!fresh) {
    const utm = INITIAL_UTM;
    let refHost = "";
    try { refHost = document.referrer ? new URL(document.referrer).hostname : ""; } catch {}
    s = {
      id: uuid(), started: now, last: now, isNew: true,
      landing_page: INITIAL_PATH,
      referrer_host: refHost && refHost !== window.location.hostname ? refHost : "",
      utm,
    };
  } else {
    s = { ...s, last: now, isNew: false };
  }
  ls.set(SESSION_KEY, JSON.stringify(s));
  return s;
}

function context(extra = {}) {
  const s = session();
  return {
    session_id: s.id,
    anonymous_id: anonymousId(),
    context: {
      page_path: window.location.pathname.slice(0, 200),
      device_type: deviceType(),
      language: normLang(document.documentElement.lang || navigator.language),
      landing_page: s.landing_page,
      referrer_host: s.referrer_host,
      utm: s.utm,
      city_id: ls.get("ponesi_selected_city_id") || undefined,
      ...extra,
    },
  };
}

let queue = [];
let timer = null;
const seen = new Set(); // in-tab dedup for identical event_ids (React rerenders / double clicks)

async function post(path, body, beacon = false) {
  try {
    const url = `${API}${path}`;
    if (beacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([JSON.stringify(body)], { type: "application/json" }));
      return;
    }
    await fetch(url, {
      method: "POST", credentials: "include", keepalive: true,
      headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
  } catch {}
}

function flush(beacon = false) {
  if (!queue.length) return;
  const events = queue.splice(0, MAX_BATCH);
  post("/analytics/events", { ...context(), events }, beacon);
  if (queue.length) schedule();
}

function schedule() {
  if (timer) return;
  timer = setTimeout(() => { timer = null; flush(); }, FLUSH_MS);
}

export function initAnalytics() {
  const s = session();
  post("/analytics/session", {
    session_id: s.id, anonymous_id: anonymousId(), landing_page: s.landing_page,
    referrer_host: s.referrer_host, utm: s.utm, device_type: deviceType(),
    language: normLang(navigator.language), city_id: ls.get("ponesi_selected_city_id") || undefined,
  });
  // One pwa_session per analytics session, and only when actually running installed.
  if (deviceType() === "pwa") track("pwa_session", { dedupe_key: "once" });
  window.addEventListener("pagehide", () => flush(true));
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush(true); });
}

// Canonical search term: lowercase, diacritics folded, whitespace collapsed. The backend
// normalises again and redacts contact-looking input, so this is only a client-side hint.
export function normalizeQuery(raw) {
  const map = { "š": "s", "đ": "dj", "č": "c", "ć": "c", "ž": "z" };
  return String(raw || "").toLowerCase().replace(/[šđčćž]/g, (c) => map[c])
    .replace(/\s+/g, " ").trim().slice(0, 40);
}

/**
 * Queue one behavioural event.
 * @param name one of the allow-listed client event names (server rejects anything else)
 * @param props {restaurant_id, product_id, category_id, city_id, dedupe_key, ...metadata}
 */
export function track(name, props = {}) {
  try {
    const { restaurant_id, product_id, category_id, city_id, dedupe_key, ...metadata } = props;
    const s = session();
    // Stable id: same logical action in the same session is stored exactly once.
    const event_id = dedupe_key ? `${name}:${s.id}:${dedupe_key}` : `${name}:${uuid()}`;
    if (seen.has(event_id)) return;
    seen.add(event_id);
    queue.push({ event_id, event_name: name, restaurant_id, product_id, category_id, city_id,
                 page_path: window.location.pathname.slice(0, 200), metadata });
    if (queue.length >= MAX_BATCH) flush(); else schedule();
  } catch {}
}

/** Current analytics session id — sent with an order so the funnel can attribute it. */
export function analyticsSessionId() {
  try { return session().id; } catch { return null; }
}

/**
 * Acquisition attribution snapshot, persisted with a B2B lead at submission so analytics no
 * longer depend on the browser session existing. Contains NO personal data.
 */
export function attributionSnapshot() {
  try {
    const s = session();
    return { session_id: s.id, anonymous_id: anonymousId(), referrer_host: s.referrer_host,
             utm: s.utm, traffic_source: (s.utm && s.utm.source) || (s.referrer_host ? "referral" : "direct") };
  } catch { return {}; }
}

/** Fire-and-forget event that must survive the page closing (cart_abandoned). */
export function trackNow(name, props = {}) {
  track(name, props);
  flush(true);
}

/** Observe elements and report an impression once per session per entity. */
export function impressionObserver(eventName, getProps, { ratio = 0.5, delay = 600 } = {}) {
  const timers = new Map();
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const el = entry.target;
      if (entry.isIntersecting && entry.intersectionRatio >= ratio) {
        if (timers.has(el)) return;
        timers.set(el, setTimeout(() => {
          const props = getProps(el) || {};
          if (props.dedupe_key) track(eventName, props);
          io.unobserve(el);
        }, delay));
      } else {
        clearTimeout(timers.get(el));
        timers.delete(el);
      }
    });
  }, { threshold: [ratio] });
  return io;
}
